import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, Activity, Database, Tag, Globe, BarChart3, Search, FileSpreadsheet } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import axios from 'axios';
import * as XLSX from 'xlsx';
import AdminDuplicateView from '../AdminDuplicateView';
// Multi-tab fix: cliente activo en sessionStorage (per-tab).
import { getStoredSelectedClientRaw } from '../../utils/clientStorage';

interface DiagnosticsProps {
  selectedTable: string;
  tables: Array<{ tableName: string; displayName: string }>;
  onTableChange: (table: string) => void;
}

interface DiagnosticStats {
  totalPublications: number;
  totalSites: number;
  totalBrands: number;
  totalCategories: number;
  productsWithBrand: number;
  productsWithoutBrand: number;
  productsWithPrice: number;
  productsWithoutPrice: number;
  duplicatedProducts: number;
  uniqueProducts: number;
  sitesWithoutPrice: { site: string; count: number }[];
}

const SystemDiagnostics: React.FC<DiagnosticsProps> = ({ selectedTable, tables, onTableChange }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DiagnosticStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDuplicateDetails, setShowDuplicateDetails] = useState(false);
  const [allProducts, setAllProducts] = useState<any[]>([]);

  const COLORS = ['#EC4899', '#06B6D4']; // Pulpou pink and cyan

  // Generate demo products for testing
  const generateDemoProducts = () => {
    const demoProducts = [];
    const sites = ['adidas.com.ar', 'nike.com.ar', 'puma.com.ar', 'newbalance.com.ar', 'reebok.com.ar'];
    const brands = ['Adidas', 'Nike', 'Puma', 'New Balance', 'Reebok', ''];
    const categories = ['Zapatillas', 'Remeras', 'Pantalones', 'Accesorios', 'Mochilas'];
    
    for (let i = 0; i < 500; i++) {
      const hasBrand = Math.random() > 0.1; // 90% have brand
      const hasPrice = Math.random() > 0.05; // 95% have price
      const siteIndex = Math.floor(Math.random() * sites.length);
      
      demoProducts.push({
        id: i + 1,
        titulo: `Producto Demo ${i + 1}`,
        Marca: hasBrand ? brands[Math.floor(Math.random() * (brands.length - 1))] : '',
        Cat: categories[Math.floor(Math.random() * categories.length)],
        precio: hasPrice ? Math.floor(Math.random() * 50000) + 5000 : 0,
        sale_price: hasPrice ? Math.floor(Math.random() * 50000) + 5000 : 0,
        sitio: sites[siteIndex],
        id_catalogo: Math.random() > 0.85 ? `CAT-${Math.floor(i/2)}` : `CAT-${i}`, // 15% duplicates
        url: `https://example.com/product-${i}`
      });
    }
    return demoProducts;
  };

  // Get formatted table date for filename
  const getTableDateName = () => {
    const table = tables.find(t => t.tableName === selectedTable);
    return table?.displayName || selectedTable;
  };

  // Export products without brand to Excel
  const exportProductsWithoutBrand = () => {
    const productsWithoutBrand = allProducts.filter((product: any) => {
      const brand = product.Marca || product.marca || product.MARCA || '';
      return !brand || brand.trim() === '';
    });

    const ws = XLSX.utils.json_to_sheet(productsWithoutBrand);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    
    const fileName = `${t('admin.diagnostics.products_without_brand_file')} - ${t('admin.diagnostics.data_from')} ${getTableDateName()}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export products without price to Excel
  const exportProductsWithoutPrice = () => {
    const productsWithoutPrice = allProducts.filter((product: any) => {
      const price = product.sale_price || product.precio || product.PRECIO || product.original_price || '';
      return !price || price === '0' || price === 0;
    });

    const ws = XLSX.utils.json_to_sheet(productsWithoutPrice);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    
    const fileName = `${t('admin.diagnostics.products_without_price_file')} - ${t('admin.diagnostics.data_from')} ${getTableDateName()}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export duplicate products to Excel
  const exportDuplicateProducts = () => {
    const duplicateCatalogIds = new Set<string>();
    const catalogIdMap = new Map<string, number>();
    
    allProducts.forEach((product: any) => {
      const catalogId = product.id_catalogo || product.ID_CATALOGO;
      if (catalogId) {
        const count = catalogIdMap.get(catalogId) || 0;
        catalogIdMap.set(catalogId, count + 1);
        if (count > 0) {
          duplicateCatalogIds.add(catalogId);
        }
      }
    });

    const duplicateProducts = allProducts.filter((product: any) => {
      const catalogId = product.id_catalogo || product.ID_CATALOGO;
      return catalogId && duplicateCatalogIds.has(catalogId);
    });

    const ws = XLSX.utils.json_to_sheet(duplicateProducts);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    
    const fileName = `${t('admin.diagnostics.duplicate_products_file')} - ${t('admin.diagnostics.data_from')} ${getTableDateName()}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const runDiagnostics = async () => {
    if (!selectedTable) {
      setError(t('admin.diagnostics.select_table_error'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Intentar cargar datos reales
      let products = [];
      try {
        // Get client headers
        const headers: any = {};
        const selectedClientStr = getStoredSelectedClientRaw();
        if (selectedClientStr) {
          try {
            const client = JSON.parse(selectedClientStr);
            if (client.clientId && client.clientId !== 'admin-mode') {
              headers['X-Client-Id'] = client.clientId;
              headers['X-Client-Name'] = client.clientName;
            }
          } catch (e) {
            console.error('Error parsing client:', e);
          }
        }
        
        const response = await axios.get(`/api/tables/${selectedTable}/data?limit=100000&offset=0`, { headers });
        products = response.data.data || [];
      } catch (apiError) {
        console.error('Error loading real data:', apiError);
        // Si falla la API, usar datos de demostración
        products = generateDemoProducts();
      }
      setAllProducts(products);

      // Calcular estadísticas
      const sites = new Set<string>();
      const brands = new Set<string>();
      const categories = new Set<string>();
      const catalogIds = new Set<string>();
      const duplicateCatalogIds = new Set<string>();
      
      let productsWithBrand = 0;
      let productsWithPrice = 0;
      const sitesPriceCount: { [key: string]: { total: number; withoutPrice: number } } = {};

      products.forEach((product: any) => {
        // Contar sitios
        const site = product.sitio || product.SITIO || product.url || 'Sin sitio';
        sites.add(site);

        // Inicializar contador de sitio
        if (!sitesPriceCount[site]) {
          sitesPriceCount[site] = { total: 0, withoutPrice: 0 };
        }
        sitesPriceCount[site].total++;

        // Contar marcas
        const brand = product.Marca || product.marca || product.MARCA || '';
        if (brand && brand.trim() !== '') {
          brands.add(brand);
          productsWithBrand++;
        }

        // Contar categorías
        const category = product.Cat || product.categoria || product.CATEGORIA || '';
        if (category && category.trim() !== '') {
          categories.add(category);
        }

        // Verificar precios
        const price = product.sale_price || product.precio || product.PRECIO || product.original_price || '';
        if (price && price !== '0' && price !== 0) {
          productsWithPrice++;
        } else {
          sitesPriceCount[site].withoutPrice++;
        }

        // Verificar duplicados
        const catalogId = product.id_catalogo || product.ID_CATALOGO;
        if (catalogId) {
          if (catalogIds.has(catalogId)) {
            duplicateCatalogIds.add(catalogId);
          }
          catalogIds.add(catalogId);
        }
      });

      // Calcular productos duplicados (todos los que tienen un id_catalogo duplicado)
      let duplicatedProductsCount = 0;
      products.forEach((product: any) => {
        const catalogId = product.id_catalogo || product.ID_CATALOGO;
        if (catalogId && duplicateCatalogIds.has(catalogId)) {
          duplicatedProductsCount++;
        }
      });

      // Preparar datos de sitios sin precio
      const sitesWithoutPrice = Object.entries(sitesPriceCount)
        .filter(([_, data]) => data.withoutPrice > 0)
        .map(([site, data]) => ({
          site,
          count: data.withoutPrice
        }))
        .sort((a, b) => b.count - a.count);

      setStats({
        totalPublications: products.length,
        totalSites: sites.size,
        totalBrands: brands.size,
        totalCategories: categories.size,
        productsWithBrand,
        productsWithoutBrand: products.length - productsWithBrand,
        productsWithPrice,
        productsWithoutPrice: products.length - productsWithPrice,
        duplicatedProducts: duplicatedProductsCount,
        uniqueProducts: products.length - duplicatedProductsCount,
        sitesWithoutPrice
      });

    } catch (err) {
      setError(t('admin.diagnostics.error'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTable) {
      runDiagnostics();
    }
  }, [selectedTable]);

  const renderDonutChart = (data: any[], title: string, exportHandler?: () => void) => {
    // Calculate total for percentages
    const total = data.reduce((sum, entry) => sum + entry.value, 0);
    
    // Custom percentage label function for outside the donut
    const RADIAN = Math.PI / 180;
    const renderPercentageLabel = ({
      cx, cy, midAngle, innerRadius, outerRadius, percent, index
    }: any) => {
      // Only show label if percentage is greater than 3%
      if (percent < 0.03) return null;
      
      // Position label outside the donut
      const radius = outerRadius + 20;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      
      return (
        <text 
          x={x} 
          y={y} 
          fill="#EC4899"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="14"
          fontWeight="700"
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 relative flex flex-col h-full min-h-[380px]">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
          {exportHandler && stats && stats.totalPublications > 0 && (
            <button
              onClick={exportHandler}
              className="p-1.5 text-gray-500 border border-gray-300 rounded-lg hover:text-green-600 hover:border-green-600 transition-colors"
              title={t('admin.diagnostics.export_excel')}
            >
              <FileSpreadsheet className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="h-56 flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 40, right: 40, bottom: 40, left: 40 }}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={65}
                paddingAngle={2}
                dataKey="value"
                label={renderPercentageLabel}
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any) => value.toLocaleString()} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Legend outside chart */}
        <div className="mt-auto pt-2 flex flex-col space-y-1 text-sm">
          {data.map((entry, index) => {
            const percent = ((entry.value / total) * 100).toFixed(1);
            return (
              <div key={`legend-${index}`} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-gray-700">
                  {entry.name}: {entry.value.toLocaleString()} ({percent}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header con selector de toma */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Activity className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-800">{t('admin.diagnostics.title')}</h3>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Selector de toma */}
          <div className="flex items-center space-x-2">
            <Database className="h-4 w-4 text-gray-500" />
            <select
              value={selectedTable}
              onChange={(e) => onTableChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('admin.diagnostics.select_table')}</option>
              {tables.map((table) => (
                <option key={table.tableName} value={table.tableName}>
                  {table.displayName}
                </option>
              ))}
            </select>
          </div>
          
          {/* Botón ejecutar diagnóstico */}
          <button
            onClick={runDiagnostics}
            disabled={loading || !selectedTable}
            className="flex items-center space-x-2 px-4 py-2 border-2 border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-300"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>{loading ? t('admin.diagnostics.executing') : t('admin.diagnostics.execute')}</span>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Resultados del diagnóstico */}
      {stats && !loading && (
        <>
          {/* Estadísticas principales */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">{t('admin.diagnostics.publications')}</p>
                  <p className="text-2xl font-bold text-blue-700">{stats.totalPublications.toLocaleString()}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-blue-500" />
              </div>
            </div>
            
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">{t('admin.diagnostics.sites')}</p>
                  <p className="text-2xl font-bold text-green-700">{stats.totalSites}</p>
                </div>
                <Globe className="h-8 w-8 text-green-500" />
              </div>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">{t('admin.diagnostics.brands')}</p>
                  <p className="text-2xl font-bold text-purple-700">{stats.totalBrands}</p>
                </div>
                <Tag className="h-8 w-8 text-purple-500" />
              </div>
            </div>
            
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">{t('admin.diagnostics.categories')}</p>
                  <p className="text-2xl font-bold text-orange-700">{stats.totalCategories}</p>
                </div>
                <Database className="h-8 w-8 text-orange-500" />
              </div>
            </div>
          </div>

          {/* Gráficos de dona */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {/* Productos con marca vs sin marca */}
            {renderDonutChart(
              [
                { name: t('admin.diagnostics.with_brand'), value: stats.productsWithBrand },
                { name: t('admin.diagnostics.without_brand'), value: stats.productsWithoutBrand }
              ],
              t('admin.diagnostics.products_with_brand'),
              stats.productsWithoutBrand > 0 ? exportProductsWithoutBrand : undefined
            )}

            {/* Productos con precio vs sin precio */}
            {renderDonutChart(
              [
                { name: t('admin.diagnostics.with_price'), value: stats.productsWithPrice },
                { name: t('admin.diagnostics.without_price'), value: stats.productsWithoutPrice }
              ],
              t('admin.diagnostics.products_with_price'),
              stats.productsWithoutPrice > 0 ? exportProductsWithoutPrice : undefined
            )}

            {/* Productos duplicados vs únicos */}
            {renderDonutChart(
              [
                { name: t('admin.diagnostics.unique'), value: stats.uniqueProducts },
                { name: t('admin.diagnostics.duplicated'), value: stats.duplicatedProducts }
              ],
              t('admin.diagnostics.products_unique_duplicate'),
              stats.duplicatedProducts > 0 ? exportDuplicateProducts : undefined
            )}
          </div>

          {/* Tabla de sitios sin precio */}
          {stats.sitesWithoutPrice.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h4 className="text-md font-semibold text-gray-800 mb-4">
                {t('admin.diagnostics.sites_without_price')}
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('admin.diagnostics.site')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('admin.diagnostics.products_without_price')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('admin.diagnostics.percentage_total')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.sitesWithoutPrice.map((site, index) => {
                      const percentage = ((site.count / stats.totalPublications) * 100).toFixed(1);
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {site.site}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {site.count.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                            {percentage}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sección de análisis detallado de duplicados */}
          {stats.duplicatedProducts > 0 && (
            <div className="mt-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <p className="text-yellow-800 font-medium">
                      {t('admin.diagnostics.duplicates_found', { count: stats.duplicatedProducts.toLocaleString() })}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDuplicateDetails(!showDuplicateDetails)}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm"
                  >
                    <Search className="h-4 w-4" />
                    <span>{showDuplicateDetails ? t('admin.diagnostics.hide_analysis') : t('admin.diagnostics.show_analysis')}</span>
                  </button>
                </div>
              </div>
              
              {showDuplicateDetails && (
                <div className="border-2 border-yellow-200 rounded-lg p-4 bg-yellow-50/50">
                  <AdminDuplicateView selectedTable={selectedTable} tables={tables} />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">{t('admin.diagnostics.executing')}...</p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!selectedTable && !loading && (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('admin.diagnostics.select_message')}</p>
        </div>
      )}
    </div>
  );
};

export default SystemDiagnostics;